import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, Typography, List, ListItem, ListItemText, Avatar, Tooltip } from '@mui/material';
import { apiService } from '../../services/apiService';

interface PostItem {
  platform: string;
  post_id: string;
  content?: string;
  url?: string;
  posted_at?: string;
  sentiment_label?: string;
  sentiment_score?: number;
}

const platformColor = (p: string): 'default' | 'primary' | 'secondary' => {
  switch ((p || '').toLowerCase()) {
    case 'x': return 'primary';
    case 'facebook': return 'primary';
    case 'instagram': return 'secondary';
    case 'tiktok': return 'secondary';
    case 'truth': return 'default';
    default: return 'default';
  }
};

const sentimentColor = (label?: string): 'default' | 'success' | 'warning' | 'error' => {
  switch ((label || '').toLowerCase()) {
    case 'positive': return 'success';
    case 'negative': return 'error';
    case 'neutral':
    default:
      return 'warning';
  }
};

export const MemberSocialWidget: React.FC<{ bioguideId: string }> = ({ bioguideId }) => {
  const [posts, setPosts] = useState<PostItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setPosts(null);
    setError(null);
    (async () => {
      try {
        const resp = await apiService.getMemberSocialPosts(bioguideId, { limit: 20, offset: 0 });
        if (!mounted) return;
        setPosts(resp?.data?.posts || []);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load social posts');
        setPosts([]);
      }
    })();
    return () => { mounted = false; };
  }, [bioguideId]);

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Recent Social Posts</Typography>
        </Box>
        {posts === null ? (
          <Typography variant="body2" color="textSecondary">Loading posts…</Typography>
        ) : posts.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No recent posts found.</Typography>
        ) : (
          <List>
            {posts.map((p, idx) => (
              <ListItem key={`${p.platform}:${p.post_id}:${idx}`} alignItems="flex-start" divider>
                <ListItemText
                  primary={
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip size="small" label={(p.platform || 'unknown').toUpperCase()} color={platformColor(p.platform)} />
                      {typeof p.sentiment_label !== 'undefined' && (
                        <Chip size="small" label={p.sentiment_label} color={sentimentColor(p.sentiment_label)} />
                      )}
                      {typeof p.sentiment_score === 'number' && (
                        <Chip size="small" variant="outlined" label={`Score: ${p.sentiment_score}`} />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {p.content || ''}
                      </Typography>
                      <Box display="flex" gap={1} mt={0.5}>
                        {p.url && (
                          <a href={p.url} target="_blank" rel="noopener noreferrer">View</a>
                        )}
                        {p.posted_at && (
                          <Tooltip title={new Date(p.posted_at).toISOString()}>
                            <span>{new Date(p.posted_at).toLocaleString()}</span>
                          </Tooltip>
                        )}
                      </Box>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default MemberSocialWidget;
